import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import { delay, hasValue } from "@repo/helpers";

import { BasicProps, DdlValue } from "./types";

export function useSelectorLogic(props: BasicProps) {
	const {
		id,
		loaded,
		changeHandler,
		values,
		selectIfSingle = false,
		autoSelect,
		autoSelectFn,
		isMulti,
		valueProp = "id",
		textProp = "value",
	} = props;
	const valueIds = useMemo(() => JSON.stringify(values?.map((v) => v[valueProp])), [values, valueProp]);
	const valueAutoSelected = useRef(false);
	const valueCleared = useRef(false);
	const [value, setValue] = useState<DdlValue | DdlValue[]>(() => getValue());

	useLayoutEffect(() => {
		valueCleared.current = false;
	}, [valueIds]);

	//Always run
	useEffect(() => {
		if (!loaded) return;
		const newValue = getValue();

		//when setting value due to auto-select, don't edit (i.e. dont cause a pending change)
		let dontEdit = selectIfSingle && !valueCleared.current && values && values.length === 1;
		dontEdit ||= hasValue(props.value) && !hasValue(newValue) && !valueIds.includes(props.value);
		dontEdit ||= !hasValue(props.value) && !hasValue(newValue);
		updateValue(newValue, true, dontEdit);
	}, [props.value, values, loaded, selectIfSingle, autoSelect]);

	async function updateValue(val: DdlValue | DdlValue[], wait = false, dontEdit = false) {
		setValue(val);
		// Delay if auto-set, to allow other updates to propagate.
		// This is specifically to solve the issue of a multi-cascade where the bottom's first change would sometimes
		// get overwritten by the cascade before it takes effect, so we slow the cascading down a bit
		if (wait) await delay(50);
		pushUpdate();

		function pushUpdate() {
			if (isMulti) updateValueMulti();
			else updateValueSingle();

			function updateValueSingle() {
				const newValue = val && (val as DdlValue).value;
				if (newValue !== props.value) changeHandler(newValue, id, dontEdit);
			}

			function updateValueMulti() {
				const propValue = props.value as any[];
				const newValue = val != null && (val as DdlValue[]).map((v) => v?.value).filter((v) => v != null);

				if (newValue == null && propValue == null) return;
				if (newValue?.length === propValue?.length && newValue.every((v) => propValue.includes(v))) return;

				changeHandler(newValue, id, dontEdit);
			}
		}
	}

	/**
	 * Wrapper around the setValue function. Allows us to know
	 * the value change came from a manual change and not an autoSelect
	 */
	function setValueFn(v: DdlValue | DdlValue[]) {
		valueAutoSelected.current = false;
		valueCleared.current = !v;
		updateValue(v);
	}

	useEffect(() => {
		if (autoSelect && !hasValue(value) && values?.length && loaded) {
			const forceValue = values.map(mapToValue)[0];
			updateValue(forceValue);
		}
	}, [props.loaded]);

	/**
	 * Format the selected value so that react-select will identify the selected value properly
	 */
	function getValue(): DdlValue | DdlValue[] {
		if (isMulti) return getValueMulti();
		else return getValueSingle(props.value);
	}

	function getValueMulti(): DdlValue[] {
		//Map each of the multi values as if was single select
		const arrValue = Array.isArray(props.value) ? props.value : [props.value];
		return arrValue.filter((v) => v != null).map((v) => getValueSingle(v));
	}

	/**
	 * Format the selected value so that react-select will identify the selected value properly
	 * @param inputValue
	 */
	function getValueSingle(inputValue: DdlValue) {
		if (selectIfSingle && !valueCleared.current && values && values.length === 1) {
			const outputValue = values[0];
			return mapToValue(outputValue);
		} else if (autoSelectFn && !valueCleared.current && !inputValue) {
			const autoSelectValue = autoSelectFn(inputValue, props.values);
			if (autoSelectValue) {
				valueAutoSelected.current = true;
				return mapToValue(autoSelectValue);
			}
		}
		if (!hasValue(inputValue)) {
			return inputValue;
		}

		if (!loaded || !values.length) return inputValue;
		const match = values.find((val) => val[valueProp] === inputValue);

		if (match === undefined) return undefined;

		return mapToValue(match);
	}

	function mapToValue(inputValue: { [x: string]: any }) {
		return {
			value: inputValue[valueProp],
			label: inputValue[textProp],
		} as DdlValue;
	}

	return { value, setValueFn, mapToValue };
}
